import type { IImportedFile } from "@via/pages/data/file-organizer/file-organizer.tsx";
import { DataSourceProvider } from "common";

export const getFileNameWithoutPath = (fullFileName: string): string => {
  const pathList = fullFileName.split("/");

  return pathList[
    pathList.length - (pathList[pathList.length - 1] === "" ? 2 : 1)
  ];
};

// when user clicks on add all files button, if none of the pattern is provided (we fallback to .), we need to remove all extensions instead of splitting from the first dot
// Example:
// getSampleName("a_R1_001.fastq.gz", "_R1_") => "a"
// getSampleName("sample-1.fastq", "-") => "sample"
// getSampleName("a.fastq", ".") => "a"
// getSampleName("a.fastq.gz", ".") => "a"
// getSampleName("a.b.fastq", ".") => "a.b"
// getSampleName("a.b..c.fq", ".") => "a.b..c"
export const getSampleName = (
  baseString: string,
  divisionChar: string
): string => {
  if (divisionChar !== ".") {
    return baseString.split(divisionChar)[0];
  }
  let result = baseString;
  const compressionExtensions = [
    ".gz",
    ".bz2",
    ".7z",
    ".xz",
    ".bgz",
    ".phar",
    ".rar",
    ".zip",
  ];
  // remove compression extensions from suffix if exists:
  for (const extension of compressionExtensions) {
    if (baseString.endsWith(extension)) {
      result = result.slice(0, -extension.length);
      break;
    }
  }
  // if another dot exists after compression extensions, remove the last one
  if (result.lastIndexOf(".") > 0) {
    result = result.slice(0, result.lastIndexOf("."));
  }

  return result;
};

export const getNameWithPath = (
  path: string,
  displayName: string,
  name: string,
  provider: DataSourceProvider
): string => {
  if (provider === DataSourceProvider.BaseSpace) {
    return name;
  }

  return `${path}/${displayName}`;
};

/**
 * Extracts the directory path from a given file path and appends the bucket name.
 *
 * @param fullName - The full name of the file including its path.
 * @param bucketName - The name of the bucket to be prepended.
 * @returns The directory path with the bucket name.
 *
 * Example:
 *   getDirectoryPath("folder1/folder2/file2.txt", "myBucket")
 *   // Returns: "myBucket/folder1/folder2"
 */
export const getDirectoryPath = (
  fullName: string,
  bucketName: string
): string => {
  const lastSlashIndex = fullName.lastIndexOf("/");

  // Edge case: If no '/' is found, return just the bucket name
  if (lastSlashIndex === -1) {
    return bucketName;
  }

  const combineChar = bucketName === "/" ? "" : "/";
  // Return the directory path prepended with the bucket name and '/'
  return bucketName + combineChar + fullName.substring(0, lastSlashIndex);
};

export const getMergedDirectoryPath = (
  pathsString: string,
  bucketName: string
) => {
  const longestCommonPath = getLongestCommonPath(pathsString);
  if (!longestCommonPath) {
    return bucketName;
  }
  const combineChar = bucketName === "/" ? "" : "/";

  return bucketName + combineChar + longestCommonPath;
};

/**
 * Finds the longest common path among a list of paths provided as a comma-separated string.
 *
 * @param {string} pathsString - The comma-separated string containing paths.
 * @returns {string} The longest common path among the provided paths.
 */
const getLongestCommonPath = (pathsString: string): string => {
  // Split the paths string into an array of paths
  const paths = pathsString.split(",").map(path => path.trim());

  // If there are no paths, return an empty string
  if (paths.length === 0) {
    return "";
  }

  // If there is only one path, return the full path without the file name
  if (paths.length === 1) {
    // Remove the file name from the single path
    const lastSlashIndex = paths[0].lastIndexOf("/");
    return paths[0].substring(0, lastSlashIndex + 1);
  }

  // Split each path into directories
  const splitPaths = paths.map(path => path.split("/"));

  // Initialize the common path
  let commonPath = "";

  // Iterate through each directory of the first path
  for (let i = 0; i < splitPaths[0].length; i++) {
    // Check if the current directory is common among all paths
    const currentDir = splitPaths[0][i];
    if (splitPaths.every(path => path[i] === currentDir)) {
      // Append the directory to the common path
      commonPath += (commonPath.length > 0 ? "/" : "") + currentDir;
    } else {
      // Break the loop if the directory is not common
      break;
    }
  }

  // Check if all paths are in the same folder by comparing the directories except the last segment
  const allInSameFolder = splitPaths.every(path => {
    // Check length and compare all segments except the last one (last segment is the name of the file)
    if (path.length !== splitPaths[0].length) {
      return false;
    }
    for (let i = 0; i < path.length - 1; i++) {
      if (path[i] !== splitPaths[0][i]) {
        return false;
      }
    }
    return true;
  });

  // Return the common path with or without wildcard based on whether all paths are in the same folder
  return allInSameFolder ? commonPath : commonPath + "/*";
};

type GroupedFiles = Record<string, IImportedFile[]>;

/**
 * Converts glob patterns containing curly bracket expansions like `{a,b}` into
 * regex alternations. For example, "file_{1,2}.txt" becomes "file_(1|2).txt".
 *
 * @param pattern - The glob pattern string.
 * @returns The regex string with alternations.
 */
export function globToRegexCurlyBrackets(pattern: string): string {
  const curlyPattern = /(.*){(.*?),(.*?)}(.*)/;
  if (curlyPattern.test(pattern)) {
    const inside = pattern.replace(curlyPattern, "$2,$3").replace(/,/g, "|");
    const replaced = pattern.replace(curlyPattern, `$1(${inside})$4`);
    if (curlyPattern.test(replaced)) {
      return globToRegexCurlyBrackets(replaced);
    }
    return replaced;
  }
  return pattern;
}

/**
 * Converts a glob pattern string into a JavaScript RegExp object.
 * Supports `{name}` placeholders to capture matching groups.
 * Translates wildcard characters such as `*` and `?` into their regex equivalents.
 *
 * @param pattern - The glob pattern string.
 * @returns A RegExp object or null if the pattern is empty.
 */
export function globToRegex(pattern: string): RegExp | null {
  if (!pattern) return null;

  pattern = pattern.replace(/\//g, "\\/");
  pattern = globToRegexCurlyBrackets(pattern);
  pattern = pattern.replace(/\{name\}/g, "(.*?)");
  pattern = pattern.replace(/\?/g, ".?").replace(/\*/g, ".*?");
  return new RegExp(`^${pattern}$`);
}

/**
 * Extracts the substring from a filename that matches the `{name}` capture group
 * in the provided regex pattern.
 *
 * @param filename - The filename without path.
 * @param regex - The RegExp object derived from the glob pattern.
 * @param useAutoMergePattern - If true, returns the first capture group from regex match;
 *                              if false, returns the part before the regex match.
 * @returns The matched substring or an empty string if no match.
 */
export function getRegexStringFromFilename(
  filename: string,
  regex: RegExp,
  useAutoMergePattern: boolean
): string {
  if (useAutoMergePattern) {
    const match = filename.match(regex);
    if (match && match[1]) return match[1];
  } else {
    return filename.split(regex)[0];
  }
  return "";
}

/**
 * Groups an array of files by the key extracted from their filenames based on a
 * glob pattern.
 *
 * @param files - Array of imported files.
 * @param pattern - The glob pattern string used for grouping.
 * @param useAutoMergePattern - Flag to control extraction method (default: true).
 * @returns An object where keys are group names and values are arrays of files.
 */
export function groupFilesByPattern(
  files: IImportedFile[],
  pattern: string,
  useAutoMergePattern = true
): GroupedFiles {
  const regex = globToRegex(pattern);
  if (!regex) throw new Error("Invalid pattern");

  const groups: GroupedFiles = {};

  for (const file of files) {
    const fileNameOnly = getFileNameWithoutPath(file.name);
    const key = getRegexStringFromFilename(
      fileNameOnly,
      regex,
      useAutoMergePattern
    );
    if (!key) continue;
    file.isSampled = true;

    if (!groups[key]) groups[key] = [];
    groups[key].push(file);
  }

  return groups;
}

export interface IPairedItem {
  original: IImportedFile | undefined;
  pair: IImportedFile | undefined;
  isChecked: boolean;
  isSampled?: boolean;
}

type GroupedPairedItems = Record<string, IPairedItem[]>;

/**
 * Groups paired files according to a pattern.
 * Only groups pairs where both original and pair match the same key.
 *
 * @param pairedList - Array of paired file items.
 * @param pattern - The auto-merge pattern string.
 * @returns An object mapping group names to arrays of paired items.
 */
export function groupPairedFilesByPattern(
  pairedList: IPairedItem[],
  pattern: string
): GroupedPairedItems {
  const regex = globToRegex(pattern);
  if (!regex) throw new Error("Invalid pattern");

  const grouped: GroupedPairedItems = {};

  for (const item of pairedList) {
    if (!item.original || !item.pair) continue;
    if (item.isSampled) continue;

    const originalName = getFileNameWithoutPath(item.original.name);
    const pairName = getFileNameWithoutPath(item.pair.name);

    const originalKey = getRegexStringFromFilename(originalName, regex, true);
    const pairKey = getRegexStringFromFilename(pairName, regex, true);

    // Only add if both original and pair matched and grouped to the same key
    if (originalKey && pairKey && originalKey === pairKey) {
      item.isSampled = true;

      if (!grouped[originalKey]) grouped[originalKey] = [];
      grouped[originalKey].push(item);
    }
  }

  return grouped;
}

export interface ITripletItem {
  fileR1: IImportedFile | undefined;
  fileR2: IImportedFile | undefined;
  fileR3: IImportedFile | undefined;
  isChecked: boolean;
  isSampled?: boolean;
}

export interface GroupedTripletItems {
  [sampleName: string]: ITripletItem[];
}

/**
 * Groups triplet files based on a pattern.
 * Only groups triplets where all three files match the same group key.
 *
 * @param tripletList - Array of triplet file items.
 * @param pattern - The auto-merge pattern string.
 * @returns An object mapping group names to arrays of triplet items.
 */
export function groupTripletFilesByPattern(
  tripletList: ITripletItem[],
  pattern: string
): GroupedTripletItems {
  const regex = globToRegex(pattern);
  if (!regex) throw new Error("Invalid pattern");

  const grouped: GroupedTripletItems = {};

  for (const item of tripletList) {
    const { fileR1, fileR2, fileR3 } = item;
    if (!fileR1 || !fileR2 || !fileR3) continue;
    if (fileR1.isSampled || fileR2.isSampled || fileR3.isSampled) continue;

    const nameR1 = getFileNameWithoutPath(fileR1.name);
    const nameR2 = getFileNameWithoutPath(fileR2.name);
    const nameR3 = getFileNameWithoutPath(fileR3.name);

    const keyR1 = getRegexStringFromFilename(nameR1, regex, true);
    const keyR2 = getRegexStringFromFilename(nameR2, regex, true);
    const keyR3 = getRegexStringFromFilename(nameR3, regex, true);

    if (keyR1 && keyR1 === keyR2 && keyR2 === keyR3) {
      item.isSampled = true;

      if (!grouped[keyR1]) grouped[keyR1] = [];
      grouped[keyR1].push(item);
    }
  }

  return grouped;
}
